import { apiFetch, apiFetchJson } from './api.ts';

export type ThemePreference = 'light' | 'dark' | 'auto';

export interface CurrentUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role: 'superadmin' | 'admin' | 'user' | 'guest' | 'service';
  /*
   * 'expired' is a pilot that has run out. Such a person can still sign in —
   * every old mail link keeps working — so the app, not the door, is what has
   * to stop having something to show them.
   */
  status: 'onboarded' | 'active' | 'inactive' | 'expired';
  organisation_id: string;
  /*
   * The organisation itself, as every payload that embeds one sends it. Null
   * where the record has no organisation. `type` is what tells our own people
   * apart from customers — 'internal' is us, and the session guard reads it to
   * keep our own sessions out of the replays.
   */
  organisation: { id: string; name: string | null; type: string } | null;
  /*
   * An absolute backend URL, versioned per picture and served immutably — so
   * it is rendered straight from an <img> and never cache-busted. A changed
   * picture arrives as a different URL on the next fetch of this record.
   */
  profile_image_url: string | null;
  language: string;
  timezone: string;
  notification_time: string;
  agenda_default_view: 'week' | 'list';
  agenda_week_starts_on: 'monday' | 'sunday';
  agenda_show_weekend: boolean;
  /* Only read while the `dark_mode` feature flag is on; the app is light without it. */
  theme: ThemePreference;
  /*
   * False for an organisation that turned the calendar integration and the
   * notetaker off. Every screen built around a calendar then has nothing to
   * show, so each one asks this rather than inferring it from "no calendar
   * connected" — which is a person's own situation, not the organisation's.
   */
  calendar_enabled: boolean;
  /* True when the organisation runs journeys: the app then shows Trajecten. */
  journeys_enabled: boolean;
  /* True when Wundo's Slack app is set up here, so an admin can link a workspace. */
  slack_enabled: boolean;
  /* True when the organisation shows its admins the Dashboard. */
  dashboard_enabled: boolean;
  /* True for a demo organisation: the app then offers practice and settings only. */
  demo: boolean;
  /* True when linking Google also asks for the Meet audio: the organisation records its own Meet calls. */
  meet_recorder_enabled: boolean;
  /* When they answered "who is your manager?"; null while the app may still ask. */
  manager_question_answered_at: string | null;
}

/*
 * No `scopes` are sent with either sign-in below, deliberately.
 *
 * better-auth APPENDS request scopes to the provider's configured ones — its
 * "override" documentation is wrong — so asking for identity scopes here would
 * add to whatever the provider declares rather than narrowing it. Identity is
 * already the provider default (Google: email/profile/openid; Microsoft: those
 * plus User.Read and offline_access), and the backend config no longer declares
 * calendar scopes, so sign-in asks for identity and nothing more.
 *
 * Calendar access is requested separately, when the user connects a calendar.
 */

export async function fetchCurrentUser(): Promise<CurrentUser> {
  return apiFetchJson<CurrentUser>('/users/me');
}

/**
 * The fields a person may change about themselves. Deliberately narrower than
 * the endpoint's schema: the backend policy refuses anything else outright, and
 * the whole request with it, so sending a field that is not here fails the save
 * rather than being quietly dropped.
 *
 * `email` is absent on purpose — it is the sign-in identity for magic links and
 * SSO, and nothing verifies a change to it yet.
 */
export interface ProfileUpdate {
  first_name?: string;
  last_name?: string | null;
  language?: string;
  notification_time?: string;
  timezone?: string;
  agenda_default_view?: 'week' | 'list';
  agenda_week_starts_on?: 'monday' | 'sunday';
  agenda_show_weekend?: boolean;
  theme?: ThemePreference;
}

export async function updateProfile(
  userId: string,
  patch: ProfileUpdate,
): Promise<CurrentUser> {
  return apiFetchJson<CurrentUser>(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

/**
 * Where sign-in should land. This app hosts the only login screen, so the staff
 * console sends people here with its own origin to be returned to; everyone
 * else comes back to this app.
 *
 * The login screen only passes a page of its own apps, and the backend checks it
 * again against the origins it serves, so it cannot be pointed at a stranger.
 */
export function signInDestination(redirect?: string): string {
  return redirect ?? window.location.origin;
}

/**
 * Where a failed provider sign-in lands: back on this screen, with the reason in
 * the URL.
 *
 * Without this the backend falls back to its own `baseURL`, which is the staff
 * console — so a customer whose sign-in failed would be shown a staff error page
 * offering to take them "back to integrations". That page exists for a different
 * job: linking a calendar, where the person is already signed in.
 *
 * The login screen is the better place regardless. Every failure here has
 * another way in worth trying, and only this screen can offer them.
 */
export function signInErrorDestination(redirect?: string): string {
  const url = new URL('/login', window.location.origin);
  if (redirect) {
    url.searchParams.set('redirect', redirect);
  }

  return url.toString();
}

/** Where the door says the browser should go once the token is spent. */
export interface SignInDestination {
  destination: string;
}

/**
 * Spend a sign-in token: the one door every token walks through, whether it
 * arrived by mail, in a chat message, or was typed in as a code.
 *
 * It is a POST because a navigation can be replayed. Corporate mail sandboxes
 * refetch the URLs a browser visited, minutes later and from their own machine,
 * so anything a token spends on a GET is spent for whoever fetches it first.
 *
 * Where the browser lands is the door's answer, not this app's: the destination
 * was settled when the token was minted, so a link cannot be repointed after it
 * left. `INVALID_TOKEN` (spent, unknown or expired) and `LINK_OTHER_DEVICE` (a
 * token bound to the browser that asked for it) arrive as an ApiError carrying
 * that code, and each screen turns it into its own message — so keep the codes
 * intact.
 */
export async function verifySignInToken(token: string): Promise<SignInDestination> {
  return apiFetchJson<SignInDestination>('/auth/sign-in/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/**
 * Claim one run of a demo pass and get the scoped guest session it comes with.
 *
 * A POST for the same reason the door above is one: a pass has a run budget,
 * and a URL that spends one on a plain fetch is emptied by the scanners and
 * sandboxes that read the mail before its reader does.
 *
 * A pass that is unknown or out of runs is not a failure — the backend answers
 * with the marketing page as the destination.
 */
export async function startDemoRun(pass: string): Promise<SignInDestination> {
  return apiFetchJson<SignInDestination>('/auth/demo/start', {
    method: 'POST',
    body: JSON.stringify({ pass }),
  });
}

/**
 * Ask for a sign-in link by email.
 *
 * The backend answers differently per case and says which: `UNKNOWN_ACCOUNT`
 * when no account holds the address, `SSO_REQUIRED` when the account's
 * organisation enforces single sign-on. Both arrive as an ApiError carrying
 * that code, and the login screen turns each into its own message — so keep
 * the codes intact rather than collapsing them into one generic failure.
 */
export async function requestSignInLink(email: string, redirect?: string): Promise<void> {
  await apiFetch('/auth/sign-in/magic-link', {
    method: 'POST',
    body: JSON.stringify({ email, callbackURL: signInDestination(redirect) }),
  });
}

/**
 * Trade the code from a sign-in mail for the token its link carries, for when
 * the mail is read on another device. The token then goes through
 * `verifySignInToken` exactly as if the link had been pressed — it is the same
 * token, so spending either shape spends both.
 */
export async function signInWithCode(email: string, code: string): Promise<string> {
  const { token } = await apiFetchJson<{ token: string }>('/auth/sign-in/code', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });

  return token;
}

/**
 * Hand off to Entra. Returns the URL to send the browser to; the backend
 * handles the callback and drops the session cookie before redirecting back.
 */
export async function startMicrosoftSignIn(redirect?: string): Promise<string> {
  const { url } = await apiFetchJson<{ url: string }>('/auth/sign-in/social', {
    method: 'POST',
    body: JSON.stringify({
      provider: 'microsoft',
      callbackURL: signInDestination(redirect),
      errorCallbackURL: signInErrorDestination(redirect),
    }),
  });

  return url;
}

/**
 * Hand off to Google. Same contract as the Entra handoff above.
 */
export async function startGoogleSignIn(redirect?: string): Promise<string> {
  const { url } = await apiFetchJson<{ url: string }>('/auth/sign-in/social', {
    method: 'POST',
    body: JSON.stringify({
      provider: 'google',
      callbackURL: signInDestination(redirect),
      errorCallbackURL: signInErrorDestination(redirect),
    }),
  });

  return url;
}

export async function signOut(): Promise<void> {
  await apiFetch('/auth/sign-out', { method: 'POST', body: '{}' });
}
